// 导入错误、加载和成功图标图片
const error = require('../assets/images/error.png');
const loading = require('../assets/images/loading.png');
const success = require('../assets/images/success.png');

// 定义toast图标类型
type ToastIconType = 'success' | 'error' | 'loading' | 'none';
// 定义toast的配置接口
export interface ToastOptions {
  content: string; // 显示的内容
  duration?: number; // 持续时间，可选
  type?: ToastIconType; // 图标类型，可选
  showMask?: boolean; // 是否显示遮罩，可选
  lockScroll?: boolean; // 是否锁定滚动，可选
}
// 定义默认的toast配置
const defaultToastOptions: ToastOptions = {
  content: '',
  duration: 1500,
  type: 'none',
  showMask: true,
  lockScroll: true,
};

/**
 * 初始化toast的样式
 */
const initToastStyle = () => {
  // 检查样式是否已经存在，若不存在则创建
  const isExistStyle = !!document.querySelector('style[name="toast-style"]');
  if (!isExistStyle) {
    const style = document.createElement('style');
    style.setAttribute('name', 'toast-style');
    // 定义toast的各种样式
    const contentStyle =
      '.toast-content{position:fixed;width:100vw;height:100vh;top:0;left:0;display:flex;align-items:center;justify-content:center;z-index:99999;}';
    const maskStyle =
      '.toast-mask{position:absolute;width:100%;height:100%;top:0;left:0;background-color:rgba(0,0,0,.3);z-index:-1;}';
    const boxStyle =
      '.toast-box{border-radius:5px;background-color:rgba(0,0,0,0.5); max-width: 250px;padding:15px;display:flex;flex-direction:column;align-items:center;justify-content:center;}';
    const iconStyle = '.toast-icon{width:40px;height:40px}';
    const textStyle = '.toast-text{margin-top: 5px;color:#fff;font-size:16px;}';
    const loadingKeyframes =
      '@keyframes loading{from {transform: rotate(0deg);} to {transform: rotate(360deg);}}';
    const loadingStyle =
      '.toast-loading{animation: loading 1.2s linear infinite;}';
    const zoomInKeyframes =
      '@keyframes zoom-in{from {transform: scale(0.1);} to {transform: scale(1);}}';
    const zoomOutKeyframes =
      '@keyframes zoom-out{from {transform: scale(1);} to {transform: scale(0.1);}}';
    const boxZoomInStyle = '.toast-box-zoom-in{animation: zoom-in 0.35s;}';
    const boxZoomOutStyle = '.toast-box-zoom-out{animation: zoom-out 0.35s;}';
    const fadeInKeyframes =
      '@keyframes fade-in{from {opacity: 0;} to {opacity: 1;}}';
    const fadeOutKeyframes =
      '@keyframes fade-out{from {opacity: 1;} to {opacity: 0;}}';
    const maskFadeInStyle = '.toast-mask-fade-in{animation: fade-in 0.35s;}';
    const maskFadeOutStyle = '.toast-mask-fade-out{animation: fade-out 0.35s;}';
    style.innerHTML = `${contentStyle}${maskStyle}${boxStyle}${iconStyle}${textStyle}${loadingKeyframes}${loadingStyle}${zoomInKeyframes}${zoomOutKeyframes}${boxZoomInStyle}${boxZoomOutStyle}${maskFadeInStyle}${maskFadeOutStyle}${fadeInKeyframes}${fadeOutKeyframes}`;
    document.head.append(style);
  }
};

/**
 * 根据toast类型获取对应的图标节点
 * @param type toast的类型
 * @returns 图标节点
 */
const getIconNode = (type: ToastIconType) => {
  const imgNode = document.createElement('img');
  imgNode.className = 'toast-icon';
  switch (type) {
    case 'success':
      imgNode.src = success;
      imgNode.alt = 'success';
      return imgNode;
    case 'error':
      imgNode.src = error;
      imgNode.alt = 'error';
      return imgNode;
    case 'loading':
      imgNode.className = 'toast-icon toast-loading';
      imgNode.src = loading;
      imgNode.alt = 'loading';
      return imgNode;
    default:
      return '';
  }
};

// 防止body滚动的事件处理函数
const wheelHandler = (evt: Event) => {
  if ((evt.target as HTMLElement).tagName === 'BODY') {
    evt.preventDefault();
  }
};

/**
 * 显示toast
 * @param options toast的配置，可以是字符串或ToastOptions对象
 */
export const showToast = (options: string | ToastOptions) => {
  // 若已有toast节点，则不再创建
  if (document.querySelector('.toast-content')) {
    return;
  }
  initToastStyle(); // 初始化toast样式
  // 合并配置项
  const toastOptions =
    typeof options === 'string'
      ? { ...defaultToastOptions, content: options }
      : { ...defaultToastOptions, ...options };
  // 如果需要锁定滚动，则添加滚动事件监听
  if (toastOptions.lockScroll) {
    document.body.addEventListener('wheel', wheelHandler);
  }
  // 根据类型获取图标节点
  const iconNode = getIconNode(toastOptions.type as ToastIconType);
  // 创建文本节点
  const textNode = document.createElement('div');
  textNode.className = 'toast-text';
  textNode.innerText = toastOptions.content;
  // 创建遮罩节点
  const maskNode = document.createElement('div');
  maskNode.className = 'toast-mask toast-mask-fade-in';
  // 创建toast盒子节点
  const boxNode = document.createElement('div');
  boxNode.className = 'toast-box toast-box-zoom-in';
  // 添加图标和文本到盒子
  if (iconNode) {
    boxNode.appendChild(iconNode);
  }
  boxNode.appendChild(textNode);
  // 创建内容节点并添加遮罩和盒子
  const contentNode = document.createElement('div');
  contentNode.className = 'toast-content';
  if (toastOptions.showMask) {
    contentNode.appendChild(maskNode);
  }
  contentNode.appendChild(boxNode);
  // 将内容节点添加到body
  document.body.appendChild(contentNode);
  // 如果设置了持续时间，则setTimeout后移除toast
  if (toastOptions.duration) {
    setTimeout(() => {
      document.body.removeEventListener('wheel', wheelHandler);
      boxNode.className = 'toast-box toast-box-zoom-out';
      maskNode.className = 'toast-mask toast-mask-fade-out';
      setTimeout(() => {
        document.body.removeChild(contentNode);
      }, 300);
    }, toastOptions.duration);
  }
};

/**
 * 隐藏当前的toast
 */
export const hideToast = () => {
  document.body.removeEventListener('wheel', wheelHandler);
  // 移除toast内容节点
  const contentNode = document.querySelector('.toast-content');
  const boxNode = document.querySelector('.toast-box');
  const maskNode = document.querySelector('.toast-mask');
  if (contentNode && boxNode && maskNode) {
    boxNode.className = 'toast-box toast-box-zoom-out';
    maskNode.className = 'toast-mask toast-mask-fade-out';
    setTimeout(() => {
      document.body.removeChild(contentNode);
    }, 300);
  }
};
